import { useEffect }                      from 'react';
import { RouteGuard }                     from './RouteGuard';
import { RequirePermission }              from './RequirePermission';
import { BFF_FEATURES }                    from 'framework/access/PermissionCatalog';
import { UI_PAGES, uiPageRequirement }     from 'framework/access/UiRegistry';
import { PermissionRequirement }           from 'framework/access/accessControl';
import { RouteEntry }                      from 'routes/routeTypes';
import NotFoundComponent                  from 'features/root/NotFoundComponent';
import { AuthRoutes }                     from 'features/auth/index';
import { RootComponent }                  from 'features/root/RootComponent';
import { Route, Routes, 
         useNavigate, 
         useLocation 
       }                                  from 'react-router';
import { CMSPilotHomePage }                  from 'features/cmsPilot/CMSPilotHomePage';
import { PilotDetailsPage }               from 'features/pilot/PilotDetailsPage';
import { RecoHomePage }                   from 'features/recommendations/home/RecoHomePage';
import RecoLayout                         from 'features/recommendations/layout/RecommendationsLayout';
import RecoUploadPage                     from 'features/recommendations/upload/RecoUploadPage';
import RecoComments                       from 'features/recommendations/comments/RecoComments';
import { WorkflowPage }                   from 'features/workflow/WorkflowPage';
import { CustomNodeSpecifications }       from 'features/workflow/CustomNodeSpecifications';
import { UpdateUserMetadata }             from 'features/workflow/UpdateUserMetadata';
import { RecoCreatePage }                 from 'features/recommendations/create/RecoCreatePage';
import { RecoEditPage }                   from 'features/recommendations/create/RecoEditPage';
import { useGlobalConfig }                from 'context/GlobalContext';
import PilotLayout                        from 'features/pilot/layout/PilotLayout';
import UtilityLayout                      from 'features/utility/layout/UtilityLayout';
import { UtilityListPage }                from 'features/utility/UtilityListPage';
import { UtilityHomePage }                from 'features/utility/UtilityDetailsPage';
import { UtilityCreationPage }            from 'features/utility/UtilityCreationPage';
import { UtilityDetailsPage }             from 'features/utility/UtilityDetailsPage';
import { WorkflowListPage }               from 'features/workflow/WorkflowListPage';
import BasePageLayout                     from 'layouts/BasePageLayout';
import { EnvironmentListPage }            from 'features/environments';
import { PilotConfigsPage }             from 'features/pilot/PilotConfigsPage';
import { PilotAssistantPage }           from 'features/pilot/PilotAssistantPage';
import { ApplicationScopePage }         from 'features/pilot/ApplicationScopePage';
import { CustomerDataGeneratorPage } from 'features/pilot/CustomerDataGeneratorPage';
import { EnvironmentCreationStatusPage } from 'features/pilot/EnvironmentCreationStatusPage';
import { DataExtractionStatus } from 'features/workflow/DataExtractionStatus';
import { UserCreationProgress } from 'features/workflow/UserCreationProgress';
import { ApplicationScopeMaster } from 'features/pilot/ApplicationScopeMaster';
import { PipelineMaster } from 'features/pilot/PipelineMaster';
import { PipelineComponentsPage } from 'features/pipeline';
import { DCXClustersPage, DCXClusterCreatePage, FeatureAccessPage, TemplatePersonalizationPage, ConfigComparePage, ConfigCompareReviewPage } from 'features/config-tools';
import CMSApplianceProfilePage               from 'features/cmsPilotProfiles/applianceProfile/CMSApplianceProfilePage';
import CMSHomeProfilePage                    from 'features/cmsPilotProfiles/homeProfile/CMSHomeProfilePage';
import { RecoDryRunPage }                    from 'features/migration';
import { RecoScorePage }                  from 'features/recommendations/score/RecoScorePage';
import { CMSPilotContentPage }               from 'features/cmsPilot/CMSPilotContentPage';
import { CMSPilotCreationStatusPage }        from 'features/cmsPilot/CMSPilotCreationStatusPage';
import { CMSPilotLayout } from 'features/cmsPilot/layout/CMSPilotLayout';
import { WidgetsSdkDemoPage } from 'components/widgets-sdk-demo/WidgetsSdkDemoPage';
import { ConfigRegistryPage, ConfigRegistryFormPage } from 'features/config-registry';
import {
  StringRegistryPage,
  StringRegistryFormPage,
  StringTemplateDetailPage,
} from 'features/string-registry';
import { TemplateDetailPage } from 'features/config-registry/pages/TemplateDetailPage';
import WysiwygPage from 'features/contentManagement/wysiwyg/WysiwygPage';
import { SurveyHome } from 'features/survey/home/SurveyHome';
import ColorPalettePage from 'features/contentManagement/colorPalette/ColorPalettePage';
import EditColorPalettePage from 'features/contentManagement/colorPalette/EditColorPalettePage';
import { DataScenariosPage } from 'features/contentManagement/dataScenarios/DataScenariosPage';
import { MediaContentPage } from 'features/contentManagement/mediaContent/MediaContentPage';
import { GlobalDataScenariosPage } from 'features/contentManagement/dataScenarios/GlobalDataScenariosPage';
import { BrandGuidelinesAdminPage } from 'features/contentManagement/brandGuidelines/BrandGuidelinesAdminPage';
import { PushUpdatesPage } from 'features/pilot/PushUpdatesPage';
import { UpdateHistoryPage } from 'features/pilot/UpdateHistoryPage';
import { IngestionConfigsPage } from 'features/pilot/IngestionConfigsPage';
import { AuditTrailPage } from 'features/audit/AuditTrailPage';
import { RbacHomePage } from 'features/rbac/home/RbacHomePage';

export const ROUTE_TREE = {
  ROOT : {path : '/', name : ''},
  AUTH : {path : '/auth/*', name : 'Auth'},
  UTILITY : {
    LAYOUT : {path : '/utility', name : 'Utility'},
    HOME : {path : '/utility/:id', name : 'Project Details'},
   
  },
  PROJECT : {
    LAYOUT : {path : '/project', name : 'Project'},
    LIST : {path : '/project/list', name : 'Projects', permissions : BFF_FEATURES.PROJECTS, uiPageId : UI_PAGES.PROJECTS.id},
    CREATE : {path : '/project/create', name : 'Create Project', permissions : BFF_FEATURES.PROJECTS},
    EDIT : {path : '/project/edit/:projectId', name : 'Update Project Details', permissions : BFF_FEATURES.PROJECTS},
    CREATE_DETAILS : {path : '/project/create/details', name : 'Project Details', permissions : BFF_FEATURES.PROJECTS},
    DETAILS : {path : '/project/details', name : 'Project Details', permissions : BFF_FEATURES.PROJECTS, uiPageId : UI_PAGES.PROJECT_DETAILS.id},
    PUSH_UPDATES : {path : '/project/push-updates', name : 'Pending Configuration Push', permissions : BFF_FEATURES.CONFIG_OPS, uiPageId : UI_PAGES.PUSH_UPDATES.id},
    UPDATE_HISTORY : {path : '/project/update-history', name : 'Update History', permissions : BFF_FEATURES.CONFIG_OPS},
    INGESTION_CONFIGS : {path : '/project/ingestion-configs', name : 'Ingestion Configurations', permissions : BFF_FEATURES.CONFIG_OPS},
    APPLICATION_SCOPE : {path : '/project/application-scope', name : 'Application Scope', permissions : BFF_FEATURES.APPLICATION_SCOPE, uiPageId : UI_PAGES.APPLICATION_SCOPE.id},
    PIPELINE_COMPONENTS : {path : '/project/pipeline-components', name : 'Pipeline Components', permissions : BFF_FEATURES.PIPELINE},


    CUSTOMER_DATA_GENERATOR : {path : '/project/customer-data-generator', name : 'Manage Users', permissions : BFF_FEATURES.SELF_SERVE_EMAIL, uiPageId : UI_PAGES.CUSTOMER_DATA_GENERATOR.id},
    ADD_NEW_USER : {path : '/project/add-new-user', name : 'Add New User', permissions : BFF_FEATURES.USER_METADATA},
    UPDATE_METADATA : {path : '/project/customer-data-generator/update-metadata', name : 'Update User Metadata', permissions : BFF_FEATURES.USER_METADATA, uiPageId : UI_PAGES.UPDATE_USER_METADATA.id},
    DATA_EXTRACTION_STATUS : {path : '/project/customer-data-generator/data-extraction-status', name : 'Data Extraction Status', permissions : BFF_FEATURES.DATA_GENERATOR},

    CONFIGS: { path: '/project/configs', name: 'Project Configs', permissions : BFF_FEATURES.CONFIG_OPS, uiPageId : UI_PAGES.PROJECT_CONFIGS.id },
    ASSISTANT: { path: '/project/assistant', name: 'Project Assistant' },
    ENVIRONMENT_CREATION_STATUS: { path: '/project/environment-creation-status', name: 'Environment Creation Status', permissions : BFF_FEATURES.ENVIRONMENTS },
    USER_CREATION_PROGRESS: {
      path: '/project/customer-data-generator/user-creation-progress',
      name: 'UserCreationProgress',
      permissions : BFF_FEATURES.USER_METADATA,
    },
    AUDIT_TRAIL: { path: '/project/audit-trail', name: 'Project Audit Trail', permissions : BFF_FEATURES.AUDIT, uiPageId : UI_PAGES.PROJECT_AUDIT.id },

  },
  CMS : {
    LAYOUT : {path : '/cms', name : 'CMS Projects'},
    PROJECT_LIST : {path : '/cms/projects', name : 'CMS Projects'},
    CONTENT : {path : '/cms/project-content', name : 'Content Management'},
    CREATION_STATUS : {path : '/cms/creation-status/:id', name : 'CMS Project Creation Status'},
    APPLIANCE_PROFILE : {path : '/cms/appliance-profile', name : 'Appliance Profile'},
    HOME_PROFILE : {path : '/cms/home-profile', name : 'Home Profile'},
    RECO_DRY_RUN : {path : '/cms/reco-dry-run', name : 'Reco Dry Run'},
    CONFIG_REGISTRY : {path : '/cms/config-registry', name : 'Configuration Registry', uiPageId : UI_PAGES.CONFIG_REGISTRY.id},
    CONFIG_REGISTRY_NEW : {path : '/cms/config-registry/new', name : 'New Configuration'},
    CONFIG_REGISTRY_DETAIL : {path : '/cms/config-registry/:documentId', name : 'Configuration Detail'},
    CONFIG_REGISTRY_TEMPLATE_DETAIL : {path : '/cms/config-registry/templates/:id', name : 'Template Detail'},
    STRING_REGISTRY : { path: '/cms/string-registry', name: 'String Registry', uiPageId : UI_PAGES.STRING_REGISTRY.id },
    STRING_REGISTRY_NEW : { path: '/cms/string-registry/new', name: 'New String' },
    STRING_REGISTRY_DETAIL : { path: '/cms/string-registry/:documentId', name: 'String Detail' },
    STRING_REGISTRY_TEMPLATE_DETAIL : { path: '/cms/string-registry/templates/:id', name: 'String Template Detail' },
    AUDIT_TRAIL : { path: '/cms/audit-trail', name: 'Registry Audit Trail' },
    RECO : {
      LAYOUT : {path : '/cms/reco', name : 'Recommendations'},
      COMMENTS : {path : '/cms/reco/comment/:id', name : 'Recommendation Comments'},
      LIST : {path : '/cms/reco/list', name : 'Recommendations'},
      CREATE : {path : '/cms/reco/create', name : 'Create Recommendation'},
      EDIT : {path : '/cms/reco/edit/:id', name : 'Edit Recommendation'},
      UPLOAD : {path : '/cms/reco/upload', name : 'Upload Recommendations'},
      SCORE : {path : '/cms/reco/score', name : 'Recommendation Score Thresholds'},
    },
    SURVEY : {
      HOME : {path : '/cms/survey', name : 'Survey'},
    },
  },
  CONTENT_MANAGEMENT : {
    WYSIWYG : { path: '/content-management/wysiwyg', name: 'CX Visual Editor', permissions : BFF_FEATURES.CX, uiPageId : UI_PAGES.CX_EDITOR.id },
    COLOR_PALETTE : { path: '/content-management/color-palette', name: 'Color Palette', permissions : BFF_FEATURES.COLOR_PALETTE, uiPageId : UI_PAGES.COLOR_PALETTE.id },
    EDIT_COLOR_PALETTE : { path: '/content-management/color-palette/edit/:standardColorId', name: 'Edit Color Palette', permissions : BFF_FEATURES.COLOR_PALETTE },
    DATA_SCENARIOS : { path: '/content-management/data-scenarios', name: 'Data Scenarios', permissions : BFF_FEATURES.CX, uiPageId : UI_PAGES.DATA_SCENARIOS.id },
    MEDIA_CONTENT : { path: '/content-management/media-content', name: 'Media Content', permissions : BFF_FEATURES.CMS_MEDIA, uiPageId : UI_PAGES.MEDIA_CONTENT.id },
  },
  PILOT : {
    LAYOUT : {path : '/pilot', name : 'Pilot'},
    
  },
  
  WORKFLOW : {
    LAYOUT : {path : '/workflow', name : 'Workflow'},
    LIST : {path : '/workflow/list', name : 'Workflows', uiPageId : UI_PAGES.WORKFLOWS.id},
    HOME : {path : '/workflow/:id', name : 'Workflow'},
    NODE_DETAILS : {path : '/workflow/node/:id', name : 'Component Specifications'},
  },
  ENVIRONMENT : {
    LAYOUT : {path : '/environment', name : 'Environment'},
    LIST : {path : '/environment/list', name : 'Environments', permissions : BFF_FEATURES.ENVIRONMENTS, uiPageId : UI_PAGES.ENVIRONMENTS.id},
  },
  APPLICATION_SCOPE_MASTER : {
    LAYOUT : {path : '/application-scope-master', name : 'Application Scope Master'},
    LIST : {path : '/application-scope-master/list', name : 'Application Scope Master', permissions : BFF_FEATURES.APPLICATION_SCOPE, uiPageId : UI_PAGES.APPLICATION_SCOPE_MASTER.id},
  },
  GLOBAL_DATA_SCENARIOS : {
    LAYOUT : {path : '/global-data-scenarios', name : 'Global Data Scenarios'},
    LIST : {path : '/global-data-scenarios/list', name : 'Global Data Scenarios', permissions : BFF_FEATURES.CX, uiPageId : UI_PAGES.GLOBAL_DATA_SCENARIOS.id},
  },
  BRAND_GUIDELINES : {
    LAYOUT : {path : '/brand-guidelines', name : 'Brand Guidelines'},
    LIST : {path : '/brand-guidelines/list', name : 'Brand Guidelines', permissions : BFF_FEATURES.CMS_MEDIA, uiPageId : UI_PAGES.BRAND_GUIDELINES.id},
  },
  PIPELINE : {
    LAYOUT : {path : '/pipeline', name : 'Pipeline'},
    COMPONENTS : {path : '/pipeline/components', name : 'Pipeline Components', permissions : BFF_FEATURES.PIPELINE},
  },
  PIPELINE_MASTER : {
    LAYOUT : {path : '/pipeline-master', name : 'Pipeline Master'},
    LIST : {path : '/pipeline-master/list', name : 'Pipeline Master', permissions : BFF_FEATURES.PIPELINE, uiPageId : UI_PAGES.PIPELINE_MASTER.id},
  },
  RBAC : {
    LAYOUT : {path : '/rbac', name : 'Access Control'},
    LIST : {path : '/rbac/list', name : 'Access Control', permissions : BFF_FEATURES.RBAC, uiPageId : UI_PAGES.ACCESS_CONTROL.id},
  },
  WIDGETS_SDK_DEMO : {
    HOME : {path : '/widgets-sdk-demo', name : 'Widgets SDK Demo'}
  },
  CONFIG_TOOLS : {
    LAYOUT : {path : '/config-tools', name : 'Config Tools'},
    DCX_CLUSTERS : {path : '/config-tools/dcx-clusters', name : 'DCX Clusters', permissions : BFF_FEATURES.DCX_CLUSTER_API, uiPageId : UI_PAGES.DCX_CLUSTERS.id},
    DCX_CLUSTERS_CREATE : {path : '/config-tools/dcx-clusters/create', name : 'Create DCX Cluster', permissions : BFF_FEATURES.CLUSTER_ATTRIBUTES},
    FEATURE_ACCESS : {path : '/config-tools/dcx-clusters/feature-access', name : 'Feature Access', permissions : BFF_FEATURES.DCX_CLUSTER_API, uiPageId : UI_PAGES.FEATURE_ACCESS.id},
    TEMPLATE_PERSONALIZATION : {path : '/config-tools/dcx-clusters/feature-template', name : 'Template Personalization', permissions : BFF_FEATURES.DCX_CLUSTER_API, uiPageId : UI_PAGES.TEMPLATE_PERSONALIZATION.id},
    CONFIG_COMPARE : {path : '/config-tools/config-compare', name : 'Compare & Sync Environments', permissions : BFF_FEATURES.CONFIG_TOOLS, uiPageId : UI_PAGES.CONFIG_COMPARE.id},
    CONFIG_COMPARE_REVIEW : {path : '/config-tools/config-compare/review', name : 'Review Config Promotion', permissions : BFF_FEATURES.CONFIG_TOOLS},
  }
}

/**
 * Composes a ROUTE_TREE entry's API `permissions` with its registry
 * `uiPageId` check (if registered + enforced) into the array form
 * RequirePermission ANDs together. Inert for entries whose page isn't yet
 * `enforced: true` — see UiRegistry.ts.
 */
const routeRequirements = (entry : RouteEntry) : PermissionRequirement | readonly PermissionRequirement[] => {
  const uiRequirement = uiPageRequirement(entry.uiPageId)
  if (!uiRequirement) return entry.permissions!
  return entry.permissions ? [entry.permissions, uiRequirement] : [uiRequirement]
}

export const AppRoutes = () => {

  const { rc } = useGlobalConfig()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    rc.uiRouter.init(navigate)
  }, [navigate, rc.uiRouter])

  useEffect(() => {
    rc.uiRouter.onLocationChange(location)
  }, [location, rc.uiRouter])
  
  return (
    <Routes>
      {/* PUBLIC */}
      <Route path={ROUTE_TREE.ROOT.path} element={<RootComponent/>} />
      
      {/* AUTH */}
      <Route path={ROUTE_TREE.AUTH.path} element={<AuthRoutes/>} />
      
      {/* FEATURE MODULES - PROTECTED */}
      <Route element={<RouteGuard/>}>

        {/* UTILITY */}
        <Route path={ROUTE_TREE.UTILITY.LAYOUT.path} element={<UtilityLayout/>}>
          <Route path={ROUTE_TREE.UTILITY.HOME.path} element={<UtilityHomePage/>}/>
        </Route>

        {/* PROJECT */}
        <Route path={ROUTE_TREE.PROJECT.LAYOUT.path} element={<PilotLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.LIST)} />}>
            <Route path={ROUTE_TREE.PROJECT.LIST.path} element={<UtilityListPage/>}/>
          </Route>
          <Route element={<RequirePermission require={ROUTE_TREE.PROJECT.CREATE.permissions!} />}>
            <Route path={ROUTE_TREE.PROJECT.CREATE.path} element={<UtilityCreationPage/>}/>
            <Route path={ROUTE_TREE.PROJECT.EDIT.path} element={<UtilityCreationPage/>}/>
            <Route path={ROUTE_TREE.PROJECT.CREATE_DETAILS.path} element={<UtilityDetailsPage/>}/>
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.DETAILS)} />}>
            <Route path={ROUTE_TREE.PROJECT.DETAILS.path} element={<PilotDetailsPage/>}/>
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.PUSH_UPDATES)} />}>
            <Route path={ROUTE_TREE.PROJECT.PUSH_UPDATES.path} element={<PushUpdatesPage/>}/>
          </Route>
          <Route element={<RequirePermission require={ROUTE_TREE.PROJECT.UPDATE_HISTORY.permissions!} />}>
            <Route path={ROUTE_TREE.PROJECT.UPDATE_HISTORY.path} element={<UpdateHistoryPage/>}/>
            <Route path={ROUTE_TREE.PROJECT.INGESTION_CONFIGS.path} element={<IngestionConfigsPage/>}/>
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.CONFIGS)} />}>
            <Route path={ROUTE_TREE.PROJECT.CONFIGS.path} element={<PilotConfigsPage />} />
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.APPLICATION_SCOPE)} />}>
            <Route path={ROUTE_TREE.PROJECT.APPLICATION_SCOPE.path} element={<ApplicationScopePage/>}/>
          </Route>
          <Route element={<RequirePermission require={ROUTE_TREE.PROJECT.PIPELINE_COMPONENTS.permissions!} />}>
            <Route path={ROUTE_TREE.PROJECT.PIPELINE_COMPONENTS.path} element={<PipelineComponentsPage />} />
          </Route>

          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR)} />}>
            <Route path={ROUTE_TREE.PROJECT.CUSTOMER_DATA_GENERATOR.path} element={<CustomerDataGeneratorPage />} />
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.UPDATE_METADATA)} />}>
            <Route path={ROUTE_TREE.PROJECT.UPDATE_METADATA.path} element={<UpdateUserMetadata />} />
          </Route>
          <Route element={<RequirePermission require={ROUTE_TREE.PROJECT.USER_CREATION_PROGRESS.permissions!} />}>
            <Route path={ROUTE_TREE.PROJECT.USER_CREATION_PROGRESS.path} element={<UserCreationProgress />} />
          </Route>
          <Route path={ROUTE_TREE.PROJECT.ASSISTANT.path} element={<PilotAssistantPage />} />
          <Route element={<RequirePermission require={ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.permissions!} />}>
            <Route path={ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.path} element={<EnvironmentCreationStatusPage />} />
          </Route>
          <Route element={<RequirePermission require={ROUTE_TREE.PROJECT.DATA_EXTRACTION_STATUS.permissions!} />}>
            <Route path={ROUTE_TREE.PROJECT.DATA_EXTRACTION_STATUS.path} element={<DataExtractionStatus />} />
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PROJECT.AUDIT_TRAIL)} />}>
            <Route path={ROUTE_TREE.PROJECT.AUDIT_TRAIL.path} element={<AuditTrailPage />} />
          </Route>
        </Route>
        
        {/* PILOT */}
        <Route path={ROUTE_TREE.PILOT.LAYOUT.path} element={<PilotLayout/>}>
          
        </Route>

        {/* CMS */}
        <Route path={ROUTE_TREE.CMS.LAYOUT.path} element={<CMSPilotLayout/>}>
          
          {/* PILOT */}
          <Route path={ROUTE_TREE.CMS.PROJECT_LIST.path} element={<CMSPilotHomePage/>}/>
          <Route path={ROUTE_TREE.CMS.CONTENT.path} element={<CMSPilotContentPage/>}/>
          <Route path={ROUTE_TREE.CMS.CREATION_STATUS.path} element={<CMSPilotCreationStatusPage/>}/>

          {/* ATTRIBUTES */}
          <Route path={ROUTE_TREE.CMS.APPLIANCE_PROFILE.path} element={<CMSApplianceProfilePage />} />
          <Route path={ROUTE_TREE.CMS.HOME_PROFILE.path} element={<CMSHomeProfilePage />} />
          <Route path={ROUTE_TREE.CMS.RECO_DRY_RUN.path} element={<RecoDryRunPage />} />

          {/* CONFIG REGISTRY */}
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CMS.CONFIG_REGISTRY)} />}>
            <Route path={ROUTE_TREE.CMS.CONFIG_REGISTRY.path} element={<ConfigRegistryPage />} />
            <Route path={ROUTE_TREE.CMS.CONFIG_REGISTRY_DETAIL.path} element={<ConfigRegistryFormPage />} />
            <Route path={ROUTE_TREE.CMS.CONFIG_REGISTRY_TEMPLATE_DETAIL.path} element={<TemplateDetailPage />} />
            <Route path={ROUTE_TREE.CMS.CONFIG_REGISTRY_NEW.path} element={<ConfigRegistryFormPage />} />
          </Route>

          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CMS.STRING_REGISTRY)} />}>
            <Route path={ROUTE_TREE.CMS.STRING_REGISTRY.path} element={<StringRegistryPage />} />
            <Route path={ROUTE_TREE.CMS.STRING_REGISTRY_NEW.path} element={<StringRegistryFormPage />} />
            <Route path={ROUTE_TREE.CMS.STRING_REGISTRY_DETAIL.path} element={<StringRegistryFormPage />} />
            <Route path={ROUTE_TREE.CMS.STRING_REGISTRY_TEMPLATE_DETAIL.path} element={<StringTemplateDetailPage />} />
          </Route>

          {/* REGISTRY AUDIT TRAIL (administration-level, project_id=GLOBAL) */}
          <Route path={ROUTE_TREE.CMS.AUDIT_TRAIL.path} element={<AuditTrailPage variant="admin" />} />

          {/* RECOMMENDATIONS */}
          <Route path={ROUTE_TREE.CMS.RECO.LAYOUT.path} element={<RecoLayout/>}>
            <Route path={ROUTE_TREE.CMS.RECO.COMMENTS.path} element={<RecoComments/>}/>
            <Route path={ROUTE_TREE.CMS.RECO.LIST.path} element={<RecoHomePage/>}/>
            <Route path={ROUTE_TREE.CMS.RECO.UPLOAD.path} element={<RecoUploadPage/>}/>
            <Route path={ROUTE_TREE.CMS.RECO.CREATE.path} element={<RecoCreatePage/>}/>
            <Route path={ROUTE_TREE.CMS.RECO.EDIT.path} element={<RecoEditPage/>}/>
            <Route path={ROUTE_TREE.CMS.RECO.SCORE.path} element={<RecoScorePage/>}/>
          </Route>

          {/* SURVEY */}
          <Route path={ROUTE_TREE.CMS.SURVEY.HOME.path} element={<SurveyHome />} />

        </Route>

        {/* Content Management - WYSIWYG Editor - top-level route (not under /cms layout) */}
        <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG)} />}>
          <Route path={ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG.path} element={<WysiwygPage/>}/>
        </Route>
        <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONTENT_MANAGEMENT.COLOR_PALETTE)} />}>
          <Route path={ROUTE_TREE.CONTENT_MANAGEMENT.COLOR_PALETTE.path} element={<ColorPalettePage/>}/>
          <Route path={ROUTE_TREE.CONTENT_MANAGEMENT.EDIT_COLOR_PALETTE.path} element={<EditColorPalettePage/>}/>
        </Route>
        <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONTENT_MANAGEMENT.DATA_SCENARIOS)} />}>
          <Route path={ROUTE_TREE.CONTENT_MANAGEMENT.DATA_SCENARIOS.path} element={<DataScenariosPage/>}/>
        </Route>
        <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONTENT_MANAGEMENT.MEDIA_CONTENT)} />}>
          <Route path={ROUTE_TREE.CONTENT_MANAGEMENT.MEDIA_CONTENT.path} element={<MediaContentPage/>}/>
        </Route>

        {/* Global Data Scenarios */}
        <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.GLOBAL_DATA_SCENARIOS.LIST)} />}>
          <Route path={ROUTE_TREE.GLOBAL_DATA_SCENARIOS.LAYOUT.path} element={<GlobalDataScenariosPage/>}/>
        </Route>

        {/* Brand Guidelines Admin */}
        <Route path={ROUTE_TREE.BRAND_GUIDELINES.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.BRAND_GUIDELINES.LIST)} />}>
            <Route path={ROUTE_TREE.BRAND_GUIDELINES.LIST.path} element={<BrandGuidelinesAdminPage />} />
          </Route>
        </Route>

        {/* WORKFLOW */}
        <Route path={ROUTE_TREE.WORKFLOW.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.WORKFLOW.LIST)} />}>
            <Route path={ROUTE_TREE.WORKFLOW.LIST.path} element={<WorkflowListPage />} />
          </Route>
          <Route path={ROUTE_TREE.WORKFLOW.HOME.path} element={<WorkflowPage />} />
          <Route path={ROUTE_TREE.WORKFLOW.NODE_DETAILS.path} element={<CustomNodeSpecifications />} />
        </Route>

        {/* ENVIRONMENT */}
        <Route path={ROUTE_TREE.ENVIRONMENT.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.ENVIRONMENT.LIST)} />}>
            <Route path={ROUTE_TREE.ENVIRONMENT.LIST.path} element={<EnvironmentListPage />} />
          </Route>
        </Route>

        {/* APPLICATION SCOPE MASTER */}
        <Route path={ROUTE_TREE.APPLICATION_SCOPE_MASTER.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.APPLICATION_SCOPE_MASTER.LIST)} />}>
            <Route path={ROUTE_TREE.APPLICATION_SCOPE_MASTER.LIST.path} element={<ApplicationScopeMaster />} />
          </Route>
        </Route>

        {/* PIPELINE MASTER */}
        <Route path={ROUTE_TREE.PIPELINE_MASTER.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.PIPELINE_MASTER.LIST)} />}>
            <Route path={ROUTE_TREE.PIPELINE_MASTER.LIST.path} element={<PipelineMaster />} />
          </Route>
        </Route>

        {/* RBAC / ACCESS CONTROL */}
        <Route path={ROUTE_TREE.RBAC.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.RBAC.LIST)} />}>
            <Route path={ROUTE_TREE.RBAC.LIST.path} element={<RbacHomePage />} />
          </Route>
        </Route>

        {/* WIDGETS SDK DEMO */}
        <Route path={ROUTE_TREE.WIDGETS_SDK_DEMO.HOME.path} element={<WidgetsSdkDemoPage />} />

        {/* CONFIG TOOLS */}
        <Route path={ROUTE_TREE.CONFIG_TOOLS.LAYOUT.path} element={<BasePageLayout/>}>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS)} />}>
            <Route path={ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS.path} element={<DCXClustersPage />} />
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONFIG_TOOLS.FEATURE_ACCESS)} />}>
            <Route path={ROUTE_TREE.CONFIG_TOOLS.FEATURE_ACCESS.path} element={<FeatureAccessPage />} />
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONFIG_TOOLS.TEMPLATE_PERSONALIZATION)} />}>
            <Route path={ROUTE_TREE.CONFIG_TOOLS.TEMPLATE_PERSONALIZATION.path} element={<TemplatePersonalizationPage />} />
          </Route>
          <Route element={<RequirePermission require={ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE.permissions!} />}>
            <Route path={ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE.path} element={<DCXClusterCreatePage />} />
          </Route>
          <Route element={<RequirePermission require={routeRequirements(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE)} />}>
            <Route path={ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE.path} element={<ConfigComparePage />} />
            <Route path={ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE_REVIEW.path} element={<ConfigCompareReviewPage />} />
          </Route>
        </Route>


      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundComponent />} />
    </Routes>
  )
}